/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable max-lines */

import { setURLParams } from 'actions';
import { fetchCohorts } from 'actions/cohorts';
import CohortStats from 'components/CohortManager/CohortStats';
import Modal from 'components/Modal';
import SelectRedesign from 'components/Select/SelectRedesign';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getCustomerDomain } from 'selectors/auth';
import { getCohorts } from 'selectors/cohorts';
import { useSelector } from 'store';
import { useHistory } from 'utils/urls';
import SeatsTable from '../SeatsTable';

type ComponentProps = {
  isModalOpen: boolean;
  toggleModalOpen: () => void;
};

const ManageCohortsModal: React.FC<ComponentProps> = ({ isModalOpen, toggleModalOpen }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const cohorts = useSelector(getCohorts);
  const customerDomain = useSelector(getCustomerDomain);

  const [cohort, setCohort] = useState<string>('');

  const cohortsOptions = cohorts.map((c) => ({ value: c.uuid, label: c.name }));
  const selectedCohort = cohorts.find((c) => c.uuid === cohort);

  useEffect(() => {
    dispatch(fetchCohorts());
  }, [dispatch]);

  const handleViewCohort = (): void => {
    dispatch(
      setURLParams({
        [`${resourceQueryParamName.user}__cohort_uuids`]: [cohort],
        [`${resourceQueryParamName.user}__offset`]: '0',
      })
    );

    toggleModalOpen();
  };

  const handleUpdateCohort = (): void => {
    history.pushLookup({
      customerDomain,
      routeName: 'cohort-users-permissions',
      routeParams: { cohortId: cohort },
    });
  };

  const handleSelectCohort = (value: string): void => {
    setCohort(value);
  };

  const getDateRange = (): string => {
    if (!selectedCohort?.config.sync_start_date) {
      return 'Not set';
    }

    return `${moment(selectedCohort.config.sync_start_date).format('MM/DD/YYYY')} - 
                  ${
                    selectedCohort.config.sync_end_date
                      ? moment(selectedCohort.config.sync_end_date).format('MM/DD/YYYY')
                      : 'Present'
                  }`;
  };

  const getDateRangeType = (): string => {
    if (!selectedCohort?.config.sync_start_date) {
      return '';
    }

    return `${
      selectedCohort.config.sync_end_date
        ? `${moment(selectedCohort.config.sync_end_date).from(
            moment(selectedCohort.config.sync_start_date),
            true
          )} `
        : 'Livestream'
    }`;
  };

  const renderModalBody = (
    <div className="w-full py-4 flex flex-col gap-4 text-body">
      <div className="w-66 flex">
        <SelectRedesign onChange={handleSelectCohort} options={cohortsOptions} value={cohort} />
      </div>
      <div className="h-56 flex flex-col gap-2">
        {selectedCohort && (
          <>
            <div className="self-start">
              <div>{getDateRange()}</div>
              <div>{getDateRangeType()}</div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="self-center">
                <SeatsTable cohortId={cohort} />
              </div>
              <CohortStats cohort={selectedCohort} />
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      {isModalOpen && (
        <Modal
          body={renderModalBody}
          title="Manage Cohort"
          okButton
          okButtonText="Update Cohort"
          okButtonDisabled={!cohort}
          okButtonOnClick={handleUpdateCohort}
          toggleShowModal={toggleModalOpen}
          cancelButton
          cancelButtonText="View Cohort"
          cancelButtonOnclick={handleViewCohort}
          cancelButtonDisabled={!cohort}
          style={{ width: '34rem' }}
        />
      )}
    </>
  );
};

export default ManageCohortsModal;
